<template>
  <div
    :class="['m-overview-card m-overview-card--desktop', cardClassName]"
    @mouseover="mouseOver"
    @mouseleave="mouseLeave"
  >
    <div class="m-overview-card__content-container">
      <div v-if="search">
        <router-link
          :to="search"
          class=".a-link--wraps-component"
          tabindex="-1"
          iconsize="text"
        >
          <div class="m-overview-card__category-label">
            {{ $t(`overview.${categoryId}`) }}
          </div>
        </router-link>
      </div>
      <div v-else>
        <div class="m-overview-card__category-label">
          {{ $t(`overview.${categoryId}`) }}
        </div>
      </div>

      <div
        ref="topCurtainAndImageContainerRef"
        class="m-overview-card__top-curtain-and-image-container"
      >
        <div class="m-overview-card__image m-overview-card__image-background">
          <!--
          Include a IIIF based image with a ROI and crop. We can work with you to identify IDs for images you want to use.
          Using them allows us to generate a srcSet for the image.
            - docs: https://github.com/thegetty/getty-ui/tree/main/src/mixins/iiifMixin -- covers many of the common parameters
            - size presets: https://github.com/thegetty/getty-ui/blob/main/src/utils/imagePresets.json -- documents the various size presets (see storybook for visualization)
            - storybook: https://5d9637d3d0f20b0020bdbea1-zyqffmcfcf.chromatic.com/?path=/story/atoms-image--using-image-id&args=
          Note the width and height which is the width and height of the full-sized image, but it also sets the ratio for the resulting image's height/width.
          One of the fun bits here is to:
            (a) identify the Region of Interest in the full-sized image, and then
            (b) to translate that into something that's going to always show in the image at the different responsive sizes well.
          -->
          <router-link
            :to="search"
            class=".a-link--wraps-component"
            tabindex="-1"
            iconsize="text"
          >
            <ImageResponsive
              :id="id"
              class="m-overview-card__responsive-image"
              size="post_medium_variable_height"
              :region="pctRegion"
              :style="{ transition: `transform ${animationDuration}s ease` }"
              :alt="data.title"
              :title="data.title"
            />
          </router-link>
        </div>
        <GuiLink :to="search">
          <div
            class="m-overview-card__top-curtain"
            :style="{
              height: `${curtainHeight}px`
            }"
          >
            <div
              class="m-overview-card__top-curtain-dynamic"
              :class="
                isHeightFull
                  ? 'm-overview-card__top-curtain-dynamic--height-100'
                  : 'm-overview-card__top-curtain-dynamic--height-0'
              "
              :style="{
                transition: `height ${animationDuration}s ease`
              }"
            >
              <div
                class="m-overview-card__percentage"
                :style="{ transition: `opacity ${animationDuration}s ease` }"
              >
                {{ formattedPercentage }}
              </div>
            </div>
          </div>
        </GuiLink>
      </div>
    </div>
    <div v-if="data.copyright != undefined" class="m-overview-card__copyright">
      {{ data.copyright }}
    </div>

    <router-link
      :to="to"
      class=".a-link--wraps-component"
      tabindex="-1"
      iconsize="text"
    >
      <div
        class="m-overview-card__bottom-curtain"
        :style="{
          transition: `max-height ${animationDuration}s ease`,
          maxHeight: `${bottomCurtainHeight}px`
        }"
      >
        <div
          ref="bottomCurtainInfoRef"
          class="m-overview-card__bottom-curtain-info"
        >
          <div class="m-overview-card__arrow-container">
            <Icon name="outbound" size="full" />
          </div>
          <div class="m-overview-card__object-info">
            <div>
              {{
                data.authors
                  .map((a) => `${a.prefix || ""} ${a.name}`.trim())
                  .join(", ")
              }},
            </div>
            <div class="m-overview-card__object-info-title">
              {{ data.title }},
            </div>
            <div>{{ data.timespanLabel }}</div>
          </div>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script>
import { ImageResponsive, GuiLink, Icon } from "@thegetty-private/getty-ui";
import { scaleLinear } from "d3-scale";
import { formatPercentage, constructSearch } from "@/helpers/overviewHelper";
const MIN_CURTAIN_HEIGHT = 5;
const LOAD_ANIMATION_DELAY_MS = 600;
export default {
  name: "OverviewCardDesktop",
  components: { ImageResponsive, GuiLink, Icon },
  props: {
    /**
     * Card id
     *
     * @type {String}
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Percentage value [0, 100] that indicates how many objects of that category the Getty Museum owns
     *
     * @type {Number}
     */
    datum: {
      type: Number,
      required: true
    },
    /**
     * Id of the category the card belongs to
     *
     * @type {String}
     */
    categoryId: {
      type: String,
      required: true
    },
    /**
     * CSS class that indicates the theme color to use
     *
     * @type {String}
     */
    cardClassName: {
      type: String,
      required: true
    },
    /**
     * String indicating the image ratio
     *
     * @type {String}
     */
    ratio: {
      type: String,
      required: true
    },
    /**
     * An Object containing the crop regions for each aspect ratio at which an image can be rendered at (full, 2:3, 16:9, no-ratio)
     *
     * @type {Object}
     *
     *  region - "full" or any of the ratios supported by the size you've chosen from the presets {@link src/utils/imagePresets.json}
     *  x - the x offset of the crop for the image at full resolution
     *  y - the y offset of the crop for the image at full resolution
     *  w - the width of the crop at the image's full resolution
     *  h - the height of the crop at the image's full resolution
     *
     * @example { "1:1": { x:0, y:0, w: 800, h: 800 }, "16:9": {...}}
     */
    region: {
      type: Object,
      required: false,
      default: undefined
    },
    /**
     * Object containing object info like title, authors and date
     *
     * @type {Object}
     *
     * @example { title: 'Bacchante with an Ape', authors: [{ name: 'Hendrick ter Brugghen ' }], timespanLabel: '1627' }
     */
    data: {
      type: Object,
      required: true
    },
    /**
     * The router-link "to" property
     */
    to: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      /**
       * Current percentage value [0, 100]
       * @type {(String | null)}
       */
      percentage: null,
      /**
       * Number (in pixels) indicating the top curtain height when it is open
       * @type {Number}
       */
      topCurtainContainerHeight: 0,
      bottomCurtainInfoHeight: 0,
      isHeightFull: false,
      isHovered: false
    };
  },
  computed: {
    /**
     * Height (in pixel) of the card top colored curtain.
     *
     * @returns {Number}
     */
    curtainHeight() {
      const heightScale = scaleLinear(
        [0, 100],
        [MIN_CURTAIN_HEIGHT, this.topCurtainContainerHeight]
      );
      const scaledHeight = heightScale(this.datum);
      return scaledHeight;
    },
    /**
     * Animation duration (in seconds). It's based on the height of the card portion that the image takes up
     *
     * @returns {Number}
     */
    animationDuration() {
      const visibleImageHeight =
        this.topCurtainContainerHeight - this.curtainHeight;
      const durationScale = scaleLinear(
        [MIN_CURTAIN_HEIGHT, this.topCurtainContainerHeight],
        [0.7, 0.5]
      );
      return durationScale(visibleImageHeight);
    },
    /**
     * Returns the percentage value to show
     *
     * @returns {String}
     */
    formattedPercentage() {
      return this.percentage !== null
        ? `${formatPercentage(this.percentage)}%`
        : "";
    },
    /**
     * Return the region object with percentage values
     *
     * @returns {Object}
     */
    pctRegion() {
      return {
        [this.ratio]: {
          ...this.region[this.ratio],
          x: `pct:${this.region[this.ratio].x}`
        }
      };
    },
    bottomCurtainHeight() {
      return this.isHovered ? this.bottomCurtainInfoHeight : 0;
    },
    /**
     * Returns a search route params Object or undefined for a GuiLink
     *
     * @return {Object} | undefined
     */
    search() {
      if (this.data.search != undefined) {
        return constructSearch(this.data.search);
      }
      return undefined;
    }
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onResize, { passive: true });
    }
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
    setTimeout(() => {
      this.isHeightFull = true;
      this.setProgressivePercentage();
    }, LOAD_ANIMATION_DELAY_MS);
  },
  methods: {
    /**
     * Compute the top curtain container height every time a window resize occurs
     */
    onResize() {
      this.computeTopCurtainContainerHeight();
      setTimeout(() => {
        this.computeBottomCurtainContainerHeight();
      }, 600);
    },
    /**
     * Compute the top curtain container height
     */
    computeTopCurtainContainerHeight() {
      if (!this.$refs.topCurtainAndImageContainerRef) return;
      this.topCurtainContainerHeight =
        this.$refs.topCurtainAndImageContainerRef.clientHeight;
    },
    /**
     * Compute the bottom curtain container height
     */
    computeBottomCurtainContainerHeight() {
      if (!this.$refs.bottomCurtainInfoRef) return;
      this.bottomCurtainInfoHeight =
        this.$refs.bottomCurtainInfoRef.clientHeight;
    },
    setIsHeightFull(isHeightFull) {
      this.isHeightFull = isHeightFull;
    },
    setIsHovered(isHovered) {
      this.isHovered = isHovered;
    },
    /**
     * Set the current percentage value of the top curtain during the growing phase.
     */
    setProgressivePercentage() {
      const percentageScale = scaleLinear()
        .domain([0, this.animationDuration])
        .range([1, this.datum])
        .clamp(true);
      let startTimestamp;
      const step = (timestamp) => {
        const timestampDiffSeconds = (timestamp - startTimestamp) / 1000;
        const percentage = Math.round(percentageScale(timestampDiffSeconds));
        if (percentage <= Math.round(this.datum)) {
          this.percentage = percentage;
          window.requestAnimationFrame(step);
        } else {
          this.percentage = this.datum;
        }
      };
      // update the percentage value  before the browser performs the next repaint
      window.requestAnimationFrame((timestamp) => {
        startTimestamp = timestamp;
        step(timestamp);
      });
    },
    mouseOver() {
      this.setIsHeightFull(false);
      this.setIsHovered(true);
    },
    mouseLeave() {
      this.setIsHeightFull(true);
      this.setIsHovered(false);
    }
  }
};
</script>

<style lang="scss">
@import "./desktop";
</style>
